import { Signal } from '@angular/core';
import { Observable } from 'rxjs';

export interface DatapointValue {
  key: string;
  label: string;
  unit?: string;
  value: number | string | null;
}

export interface CompareEntry {
  key: string;
  label: string;
  delta: number;        // absolute difference (this - neighbour)
  pct: number | null;   // percentage change vs the neighbour; null when undefined
}

export interface DatapointAttachment {
  type: 'csv';
  ingestedCsvId: string;
  filename?: string;
  status?: string;       // 'processed' | 'removed' | 'orphan' | 'error'
}

export interface DatapointView {
  sourceLabel: string;                  // "Channel 3" / measurement name
  timestamp: Date;
  values: DatapointValue[];
  highlightKey?: string;                // the clicked `variable`, rendered emphasised
  nav: { index: number; total: number; hasPrev: boolean; hasNext: boolean };
  compare: { prev?: CompareEntry[]; next?: CompareEntry[] };
  attachments: DatapointAttachment[];
}

/**
 * The config object for the datapoint details panel. Per measurement type,
 * instantiated by the measurement component with direct access to its loaded
 * data. Stateful: holds a cursor; next()/prev() recompute `current` from
 * in-memory vectors.
 */
export interface DatapointAdapter {
  readonly current: Signal<DatapointView>;
  next(): void;
  prev(): void;
  readonly canDelete: Signal<boolean>;
  delete(): Observable<unknown>;   // wired for SHM in Phase 3; other types return of(null)

  /** For the removal confirm dialog — how many downstream (cumulative) values
   *  would be recomputed if the current datapoint were removed. Optional
   *  (SHM sets it; single-vector types omit it). */
  readonly affectedCount?: Signal<number>;
}

/** Percentage change from `from` to `to`. null when `from` is 0/missing or inputs aren't numbers. */
export function pctDelta(from: number | null | undefined, to: number | null | undefined): number | null {
  if (typeof from !== 'number' || typeof to !== 'number' || !isFinite(from) || !isFinite(to)) return null;
  if (from === 0) return null;
  return ((to - from) / Math.abs(from)) * 100;
}
